import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authService } from 'auth/services';
import classNames from 'classnames';

import { useIsAuthenticated } from '@azure/msal-react';

import { ArrowIcon } from 'features/order-tracker/shared-components/svg';
import { updateAppointmentDateAndTime } from 'features/order-tracker/store/actions';
import { getAmendAppointmentInfo } from 'features/order-tracker/store/selectors';
import { formatDate, getYearForNextSelectedMonth } from 'features/order-tracker/utils/dateUtils';

import { DayWithSlots, SlotsLoader } from './components';
import { AVAILABLE_SLOTS_TITLE, OTHER_SLOTS_AVAILABLE_TITLE, VIEW_MORE_DATES } from './constants';

import styles from './styles.module.scss';

export const AvailableSlotsInfo: React.FC = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { selectedDate, selectedSlot, fetching, preAppointments } = useSelector(getAmendAppointmentInfo);

  const [firstAppointment, ...otherAppointments] = preAppointments;
  const [showMore, setShowMore] = useState(false);
  const selectSlot = (slotInfo: string, date: Date): void => {
    date.setFullYear(getYearForNextSelectedMonth(date.getMonth()));
    const isSlotAlreadySelected = slotInfo === selectedSlot;

    isAuthenticated && authService.throttledRefreshToken();
    dispatch(
      updateAppointmentDateAndTime({
        selectedDate: formatDate('YYYY-MM-DD', date),
        selectedSlot: isSlotAlreadySelected ? '' : slotInfo,
      }),
    );
  };

  if (fetching || !preAppointments.length) {
    return <SlotsLoader />;
  }

  return (
    <div className={styles.availableSlotsContainer}>
      <h4 className={styles.availableSlotsTitle}>{AVAILABLE_SLOTS_TITLE}</h4>
      <DayWithSlots
        title={firstAppointment.formattedDate}
        availableSlots={firstAppointment.availableSlots}
        selectSlot={selectSlot}
        selectedSlot={selectedSlot}
        dateInfo={firstAppointment.dateInfo}
      />
      {!!otherAppointments.length && (
        <>
          <button
            className={styles.viewMoreDatesButton}
            onClick={() => setShowMore((prevValue) => !prevValue)}
            type="button"
          >
            <p>{VIEW_MORE_DATES}</p>
            <ArrowIcon width={7} height={7} className={classNames(styles.arrowIcon, { [styles.expanded]: showMore })} />
          </button>
          <div className={classNames(styles.otherDaysContainer, { [styles.hidden]: !showMore })}>
            <h4 className={classNames(styles.otherSlotsAvailableTitle, { [styles.hidden]: !selectedDate })}>
              {OTHER_SLOTS_AVAILABLE_TITLE}
            </h4>
            {otherAppointments.map((appointment) => (
              <DayWithSlots
                key={appointment.dateInfo}
                dateInfo={appointment.dateInfo}
                title={appointment.formattedDate}
                availableSlots={appointment.availableSlots}
                selectSlot={selectSlot}
                selectedSlot={selectedSlot}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
